import { APP_VERSION, BUILD_DATE } from '../../version';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    type: 'added' | 'changed' | 'fixed' | 'backend';
    items: string[];
  }[];
}

const changelog: ChangelogEntry[] = [
  {
    version: '3.1.0',
    date: '2026-09-24',
    sections: [
      {
        type: 'added',
        items: [
          'Click any row on the By SKU, By Client, or By Renewal tabs of the Margin Audit to expand it and see the exact subscription lines that produced the aggregate — qty, sell, cost, MSRP, margin, commitment, renewal. Below-cost lines are highlighted red inside the drilldown. Each tab tracks its own open rows independently.',
        ],
      },
    ],
  },
  {
    version: '3.0.3',
    date: '2026-09-23',
    sections: [
      {
        type: 'fixed',
        items: [
          'Real fix for the Margin Audit "Not authenticated" issue. Azure App Service strips any inbound HTTP header starting with x-ms-*, which meant the browser-set x-ms-client-principal header never reached the function code — no matter what size it was. Frontend now sends the principal under a different name (x-swa-client-principal) that the platform lets through; the middleware accepts either.',
        ],
      },
    ],
  },
  {
    version: '3.0.2',
    date: '2026-09-23',
    sections: [
      {
        type: 'fixed',
        items: [
          'Direct API calls (used by the Margin Audit and any endpoint that bypasses the SWA proxy) returned "Not authenticated" for staff who belonged to a lot of Entra groups. The auth header hit Azure Function App\'s 8KB size limit and got truncated. Now sends a slimmed principal on those calls so the header stays under the limit; identity is unaffected.',
        ],
      },
    ],
  },
  {
    version: '3.0.1',
    date: '2026-09-23',
    sections: [
      {
        type: 'fixed',
        items: [
          'The new /margin-audit page fell through to the dashboard when accessed from the main app URL. The route was registered under the HUD-iframe embed mode branch only; now registered under the main non-embed branch as well.',
        ],
      },
    ],
  },
  {
    version: '3.0.0',
    date: '2026-09-23',
    sections: [
      {
        type: 'added',
        items: [
          'New Margin Audit page under Licensing. Walks every active Pax8 client and every in-scope subscription, joins each line against the catalog MSRP and partner cost, and surfaces below-cost bleeders + money left on the table. Four tabs: By SKU (which price hike hit which product), By Client (who to talk to first, sorted by monthly bleed), By Renewal (which locked commitments release which month), All Lines (full detail with below-cost rows highlighted). Filter by text, below-cost only, or below-MSRP only. Export CSV button downloads the filtered view.',
          'The audit takes 30-45 seconds to walk the whole book — it hits the Function App directly to bypass the SWA proxy timeout. First load shows a spinner; refresh button on the header re-runs it on demand.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Bumped frontend to 3.0.0 for the new top-level surface.',
        ],
      },
    ],
  },
  {
    version: '2.99.13',
    date: '2026-09-23',
    sections: [
      {
        type: 'fixed',
        items: [
          'Margin audit MSRP column came back empty on every row. Pax8 splits product metadata and pricing between two separate API endpoints; the audit was only fetching metadata. Now fetches both in parallel and merges so every row lands with real MSRP and can flag AT/ABOVE/BELOW_MSRP.',
        ],
      },
    ],
  },
  {
    version: '2.99.12',
    date: '2026-09-23',
    sections: [
      {
        type: 'added',
        items: [
          'New endpoint GET /api/pax8/margin-audit runs a portfolio-wide license margin audit — walks every active client, joins each subscription against the Pax8 catalog priceLists (billingTerm + commitmentTerm match), and emits a per-line report with monthly bill, cost, margin, margin %, % of MSRP, and flags for below-cost and below-MSRP lines. Returns CSV by default so you can drop it straight into a spreadsheet, or JSON with a portfolio-level rollup when format=json. Filters: belowCostOnly, belowMsrpOnly, single companyId, includeInactive.',
        ],
      },
    ],
  },
  {
    version: '2.99.11',
    date: '2026-09-23',
    sections: [
      {
        type: 'added',
        items: [
          'New endpoint GET /api/pax8/product/{productId} returns the full Pax8 catalog product plus its pricing tiers (MSRP and partner cost per billing/commitment term). Unlocks portfolio-wide license margin auditing that previously required MSRP lookups Pax8 exposes only at the product level, not the subscription level.',
        ],
      },
    ],
  },
  {
    version: '2.99.6',
    date: '2026-08-18',
    sections: [
      {
        type: 'backend',
        items: [
          'DMARC report ingest now uses a dedicated least-privilege Entra app (04f60c5a) instead of the shared cross-tenant reports app. The new app has Mail.ReadWrite scoped by an Exchange Application Access Policy to the dmarc@umetech.net mailbox only — Test-ApplicationAccessPolicy confirms every other mailbox in the tenant is denied. This unblocks the ingest pipeline (the shared app only had Mail.Read, so PATCH /messages/{id} to mark reports as processed was 403ing every 15 minutes and no reports were making it into Cosmos or the weekly digest).',
          'Cert-based auth: the cert lives in Key Vault, is pulled at runtime via the Function App\'s Managed Identity (Secrets User role scoped to that one secret), and parsed in-process — no plaintext ever hits disk or config. If the cert ever leaks, blast radius is one mailbox with zero sensitive content.',
        ],
      },
    ],
  },
  {
    version: '2.99.5',
    date: '2026-08-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'REAL root cause of Email Health "Run Check" 10-minute hangs on newly-onboarded clients: a copy-paste typo in the save-check function caused it to recurse into itself instead of writing the record. Each recursion did another Cosmos read and re-checked the fingerprint, looping forever until Azure killed the function. It only bit clients whose check result differed from the previous — which is always the case for a client on their first check. Fixed.',
          'Also wrapped every Graph API call in the check with a 30-second timeout (matching v2.99.4\'s Automation-side wrappers), and added a 180-second total-budget guard at the endpoint so the caller always gets a coherent "check timed out" result if anything hangs — never a raw 504 from Azure Front Door.',
        ],
      },
    ],
  },
  {
    version: '2.99.4',
    date: '2026-08-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Email Health "Run Check" no longer hangs for 10 minutes and returns "Failed to fetch" for newly-onboarded clients. Root cause was that the Azure Automation runbook calls (used to inspect Exchange Online connectors) had no request-level timeout, so a single stalled call would pin the check to the platform max. Every runbook API call now has a hard 30-second timeout, and Azure token acquisition has a 15-second timeout, so bounded execution is guaranteed regardless of how Azure responds.',
        ],
      },
    ],
  },
  {
    version: '2.99.3',
    date: '2026-08-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Email Health check no longer falsely reports "MX does not route through SpamHero" for tenants where the SpamHero-routed domain isn\'t the M365 default. The check now looks at every verified custom domain, not just the default one, and picks the SpamHero-routed domain as the primary. Fixes The Newport Group and any other client with multiple mail-enabled domains.',
        ],
      },
    ],
  },
  {
    version: '2.99.2',
    date: '2026-08-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Email Health "Run Check" (and "Fix Enhanced Filtering") returned "Not authenticated" when the portal was loaded inside the UMT HUD iframe. Both actions call the API directly (they exceed the standard proxy timeout), and that direct call wasn\'t forwarding the embed-mode auth headers the middleware needs when the portal is embedded. Now it does.',
        ],
      },
    ],
  },
  {
    version: '2.99.1',
    date: '2026-07-24',
    sections: [
      {
        type: 'fixed',
        items: [
          '"Generate HIPAA Report" was falsely showing "Authentication required" — the report\'s legitimate HTML body was being misread as an auth-redirect page. Fixed. Refresh Now was unaffected.',
        ],
      },
    ],
  },
  {
    version: '2.99.0',
    date: '2026-07-24',
    sections: [
      {
        type: 'added',
        items: [
          'New "Compliance" item in the sidebar (Security section) → a cross-client compliance overview at /compliance.',
          'Framework tabs (HIPAA live; SOC 2 / PCI / GDPR marked "soon"), portfolio stat tiles (avg score, count below 70, never-assessed, data gaps), and a worst-first client table with per-row Report + Open actions.',
          'Click any client row to jump into its compliance detail page. Search box to filter clients.',
          'Backend: new GetCompliancePortfolio endpoint aggregates all clients\' cached posture in two bulk queries. 15 new tests; 141/141 API tests pass.',
          'Scheduled + emailed compliance reports are coming in a later phase — this is the read-only overview first.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.61.0 — GET /api/security/compliance-portfolio/{framework?}. detectExoStub moved to shared for reuse by the portfolio aggregator.',
        ],
      },
    ],
  },
  {
    version: '2.98.1',
    date: '2026-07-24',
    sections: [
      {
        type: 'fixed',
        items: [
          'Compliance page "Refresh Now" and "Generate HIPAA Report" were failing with "Not authenticated". They used a direct cross-origin call to the Function App; switched them to the same-origin proxy path the rest of the app uses.',
          'The refresh runbook takes ~90s and the proxy times out at 45s — the page now recognizes this as "still running in the background" and auto-updates once the data lands, instead of showing an error.',
        ],
      },
    ],
  },
  {
    version: '2.98.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 3c: "Generate HIPAA Report" button now works end-to-end.',
          'Click it and a printable, client-ready HIPAA compliance report opens in a new tab. Use your browser\'s Print dialog to save as PDF.',
          'Report includes: cover page, executive summary with color-coded score + verdict + counts, findings sorted by severity (each with detail + remediation), passing rules list, license inventory, and methodology footer.',
          'Umetech-branded (dark blue / light blue palette) matching the existing GDAP-toolkit reports. Print CSS handles page breaks and avoids splitting finding cards.',
          'Data-availability advisory renders when the source snapshot came back without full Exchange data (Phase 2c partial-success case), so customers understand why DLP/retention gaps show up.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.60.0 — new hipaaReportTemplate.ts pure HTML generator + GenerateHipaaReport endpoint at GET /api/security/compliance/{clientId}/report/hipaa. 27 new template unit tests, 126/126 total passing.',
        ],
      },
    ],
  },
  {
    version: '2.97.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 2d + 2e: automated monthly sync + on-demand refresh.',
          'New ScheduledComplianceSync timer fires the 1st of every month at 3am UTC. Iterates all M365 clients sequentially, 5s pause between them. Logs per-client success/failure and a final rollup.',
          'New "Refresh Now" button on the CompliancePage — click and it collects fresh compliance data from the tenant in ~90 seconds. The existing button was renamed to "Reload" (fetches cached data instantly).',
          'Compliance sync no longer runs on every manual "Sync Now" click — that path is for the fast daily categories. Compliance has its own cadence.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.59.0 — new complianceSyncOrchestrator shared between the timer and the endpoint. New TriggerComplianceSync POST endpoint at /security/compliance/{clientId}/sync. SyncClientData no longer touches compliance.',
        ],
      },
    ],
  },
  {
    version: '2.96.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'changed',
        items: [
          'Compliance posture module — Phase 2c: EXO fetcher went from stub to live. Get-CompliancePosture runbook is now uploaded and published in Azure Automation and running against real tenants. Smoke-tested against Sylvia Gisi and collected mailboxes, transport rules, IRM, connectors, and org audit.',
          'Runbook refactored to a partial-success model: DLP and retention cmdlets fail on the older ExchangeOnlineManagement module in Azure Automation (SCC REST cmdlets not proxied), so those fail with warnings instead of nuking the whole snapshot. HIPAA evaluator still flags them as gaps — correct behavior since we don\'t know their state.',
          'To unblock DLP + retention: update the ExchangeOnlineManagement module in the automation account, or migrate the runbook to PowerShell 7.2.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.58.0 — complianceDeps.ts calls the real runbook via startGetCompliancePostureJob + waitForRunbookCompletion. 5min timeout for cold-start. ExoPosturePayload.warnings surfaces non-fatal per-phase failures.',
        ],
      },
    ],
  },
  {
    version: '2.95.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 3b: per-client HIPAA compliance dashboard page at /compliance/:clientId.',
          'Score badge (color-coded), findings list sorted by severity with expandable cards, collapsible passed-rules section, staleness badge, and a stub-mode advisory banner (renders when Exchange data is unavailable in the snapshot).',
          'Access it from the Client Detail action bar via the new "HIPAA Compliance" link — shown for M365 clients with a tenantId.',
          'Design system parity with the recent DMARC tab (Tailwind tokens, Lucide icons, dark-mode aware).',
        ],
      },
    ],
  },
  {
    version: '2.94.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 3a: new GetCompliancePosture API endpoint. Reads the cached posture written by SyncClientData and runs the requested framework evaluator (HIPAA today; SOC2/PCI/GDPR slot in later).',
          'Route: GET /api/security/compliance/{clientId}/{framework?}. Framework defaults to hipaa; unsupported frameworks 400.',
          'Frontend will get isExoStubbed flag to detect when the cached snapshot came from the Phase 2b EXO stub, so it can render a "data unavailable" advisory instead of alarming customers with false gaps.',
          'Pure builder + stub-detector + framework-parser separated from I/O; 15 new tests cover every branch without mocking Cosmos.',
          '95/95 tests passing.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.57.0 — new api/GetCompliancePosture/index.ts endpoint, registered under fresh security/compliance/* prefix to avoid the existing security/{param?} route conflicts.',
        ],
      },
    ],
  },
  {
    version: '2.93.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 2b: live wire-up of the sync collector into the daily M365 sync path.',
          'New Graph fetcher for Security Defaults, real Azure Automation launcher for Get-CompliancePosture runbook, pure mappers turning Graph/Pax8 source data into the compliance shape.',
          'EXO fetcher intentionally STUBBED — Compliance reports will show gaps for DLP/retention/OME/transport-rules until Get-CompliancePosture.ps1 is validated in Azure Automation. Warns to logs so it never silently pretends.',
          'SyncClientData now populates compliancePosture on every M365 client sync. Cached snapshot ready for the framework evaluators to consume in Phase 3.',
          '80/80 tests passing including 15 new deps mapper tests. Both API and frontend TypeScript build clean.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.56.0 — graphClient.getSecurityDefaultsEnabled(), automationClient.startGetCompliancePostureJob(), new api/shared/complianceDeps.ts, cosmosClient.updateCompliancePostureSync(), SyncClientData integration. CollectComplianceArgs.clientId → pax8CompanyId refactor to avoid an extra Cosmos hop.',
        ],
      },
    ],
  },
  {
    version: '2.92.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 2a: sync collector + Exchange runbook. Not yet wired into daily sync — Phase 2b lands the live integration.',
          'New EXO runbook Get-CompliancePosture.ps1 — Exchange + Purview slice of the ad-hoc HIPAA audit script, running via GDAP cert auth in Azure Automation.',
          'New complianceCollector.ts — pure assembler + parallel-fetch orchestrator with clean DI seams (Graph, runbook, Pax8 as injected deps). All-or-nothing failure model per sync category convention.',
          '11 unit tests including actual concurrency verification via a deferred gate. Total suite now 65/65 passing.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.55.0 — api/shared/complianceCollector.ts + api/runbooks/Get-CompliancePosture.ps1. No wire-up to SyncClientData yet; that arrives in Phase 2b once the runbook is validated live.',
        ],
      },
    ],
  },
  {
    version: '2.91.0',
    date: '2026-07-23',
    sections: [
      {
        type: 'added',
        items: [
          'Compliance posture module — Phase 1 foundation. Groundwork for per-client on-demand HIPAA/SOC2/PCI reports, spun out of the Sylvia Gisi HIPAA audit.',
          'New SyncedCompliancePosture types on ClientSyncData: mailboxes with audit state, transport rules, DLP policies + rules, retention policies, OME/IRM config, TLS connectors, Security Defaults, Conditional Access, per-user auth methods, license inventory.',
          'New api/shared/complianceFrameworks/ pluggable evaluator layer. HIPAA evaluator (hipaa.ts) covers 10 rules: mailbox audit, MFA enforcement, OME availability, TLS inbound, per-user MFA, PHI-aware DLP, auto-encrypt transport rule, 6-year retention, SKU coverage.',
          'Framework-agnostic shape (ComplianceGap / ComplianceEvaluation / runRules) so SOC2 / PCI / GDPR slot in later with the same UI.',
          'No user-visible UI yet. Phase 2 will add the sync fetcher, GetCompliancePosture endpoint, and per-client dashboard.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.54.0 — new api/shared/complianceFrameworks/ module with pure-logic HIPAA rules. 47 unit tests, 100% branch coverage on new code.',
        ],
      },
    ],
  },
  {
    version: '2.90.0',
    date: '2026-05-14',
    sections: [
      {
        type: 'added',
        items: [
          'DMARC Aggregate Report Ingestion — new shared mailbox dmarc@umetech.net, ingestion timer runs every 15 minutes, parses zip/gzip/XML attachments per RFC 7489.',
          'DMARC Tab on Client Detail — current policy, 30-day message volume + pass rate, top sending IPs with pass/fail tally, recent ingested reports. Live for JMG (jmgent.com).',
          'Weekly DMARC Digest Email — Mondays 9am Pacific. Portfolio summary: totals, pass rate, clients still on p=none, top failing IPs per client.',
          'Phase 0 setup for JMG — created dmarc@umetech.net shared mailbox via runbook, updated jmgent.com DMARC rua, added external-rua authorization TXT.',
        ],
      },
      {
        type: 'backend',
        items: [
          'API 1.53.5 — vLLM model discovery cache changed from process-lifetime to 60s TTL. Future GPU-side model swaps self-heal within a minute; no Function App restart needed. Regression test pins the flip behavior.',
          'API 1.53.4 — replaced hardcoded vLLM model name with dynamic discovery via /v1/models. Survives the gpt-oss-20b → Qwen3.6-35B-A3B-FP8 rename without code changes.',
        ],
      },
    ],
  },
  {
    version: '2.87.0',
    date: '2026-04-03',
    sections: [
      {
        type: 'added',
        items: [
          'Google Workspace Integration — full support for Google Workspace clients. Daily sync of users, MFA, licenses, admin roles, OAuth apps, mobile devices, and admin audit.',
          'Workspace-aware client detail — tabs adapt for M365, Google, or hybrid clients.',
          'Google OAuth Apps, Mobile Devices, and Admin Audit frontend components.',
          'DKIM check for Google domains (google._domainkey selector).',
        ],
      },
    ],
  },
  {
    version: '2.86.0',
    date: '2026-04-02',
    sections: [
      {
        type: 'added',
        items: [
          'Google Workspace API Client — JWT auth with domain-wide delegation for multi-tenant Google Workspace access.',
          'Google Workspace Users endpoint — pull users with MFA status from Google Workspace tenants.',
        ],
      },
    ],
  },
  {
    version: '2.85.0',
    date: '2026-04-02',
    sections: [
      {
        type: 'added',
        items: [
          'Contact Picker — spam upload form now lists existing client contacts in a dropdown. Auto-fills name and email.',
          'Spam Samples moved to Client Detail — now under the Email Health tab with client pre-selected.',
          'Email Health Alerting — daily 2:30am alert system flags DMARC/SPF/DKIM issues and regressions, sends HTML summary email.',
        ],
      },
    ],
  },
  {
    version: '2.84.0',
    date: '2026-04-02',
    sections: [
      {
        type: 'added',
        items: [
          'SPF DNS Lookup Counter — Email Health now counts total recursive DNS lookups in SPF records (RFC 7208 max: 10). Warns when at limit, alerts when over.',
          'SPF lookup count badge and progress bar in Email Health UI with color-coded severity.',
        ],
      },
    ],
  },
  {
    version: '2.83.0',
    date: '2026-04-02',
    sections: [
      {
        type: 'added',
        items: [
          'Spam Sample Upload Portal — secure tokenized links for clients to upload spam emails. Auto-parses headers for SPF/DKIM/DMARC results, routing, and originating IP.',
          'Spam Samples panel in Email Health — create requests, send branded emails, view parsed analysis.',
        ],
      },
    ],
  },
  {
    version: '2.82.7',
    date: '2026-04-01',
    sections: [
      {
        type: 'changed',
        items: [
          'Structured DNS record cards in Email Health — SPF/DKIM/DMARC modals now show individual record cards with per-field copy buttons (Host and Value). Step-by-step fix instructions included.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Copy button works reliably (textarea + execCommand fallback).',
          'DNS Host shows subdomain only (selector1._domainkey, _dmarc, @) — matches DNS provider expectations.',
          'Old cached results auto-parse into structured DNS record cards.',
        ],
      },
    ],
  },
  {
    version: '2.82.0',
    date: '2026-04-01',
    sections: [
      {
        type: 'added',
        items: [
          'SPF/DKIM/DMARC checks in Email Health — DNS authentication checks run for all clients. Shows SPF record status, DKIM Microsoft selectors, and DMARC policy strength in a dedicated section with history tracking.',
        ],
      },
    ],
  },
  {
    version: '2.81.0',
    date: '2026-03-26',
    sections: [
      {
        type: 'added',
        items: [
          'Batch Mailbox Editor — queue multiple delegate changes, forwarding, and out-of-office in one shot. Single Exchange connection (~90s total instead of 90s per change).',
        ],
      },
    ],
  },
  {
    version: '2.80.0',
    date: '2026-03-25',
    sections: [
      {
        type: 'added',
        items: [
          'Global Job Tracker — floating panel shows all background operations (EF fix, offboard, sync). Persists across page navigation with spinner, success, and failure states.',
          'Multiple delegates in offboard wizard — add multiple users for mailbox access and OneDrive sharing.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'CRITICAL: Offboard runs Exchange ops BEFORE license removal — prevents mailbox deletion.',
          'UserDetail Mailbox tab reads from cache — no live Exchange runbook on click.',
        ],
      },
    ],
  },
  {
    version: '2.79.1',
    date: '2026-03-25',
    sections: [
      {
        type: 'added',
        items: [
          'Duplicate name warning on Create User — warns when a user with the same display name already exists, showing existing UPN(s).',
          'Exchange operation live status tracking — offboard wizard polls runbook job, shows spinner → success/failure with toast notification.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Offboard wizard: Exchange operations no longer show "(manual)" — they are automated via Azure Automation runbook.',
        ],
      },
    ],
  },
  {
    version: '2.79.0',
    date: '2026-03-24',
    sections: [
      {
        type: 'added',
        items: [
          'Site permissions caching — SharePoint site permissions collected during 24h sync and served instantly from cache (was 30-60s live runbook per click).',
          'Conditional Access policy management — Full CRUD: list, create, update, enable/disable, delete CA policies via Graph API.',
          'Distribution list management — Create and delete distribution lists via Azure Automation runbooks.',
          'Notification pipeline — Email alerts for CRITICAL delta findings, wired into post-collection pipeline.',
          'EXO runbooks deployed — Delete-SharedMailbox, Manage-MailboxForwarding, Manage-OutOfOffice now fully operational.',
        ],
      },
    ],
  },
  {
    version: '2.70.1',
    date: '2026-03-24',
    sections: [
      {
        type: 'added',
        items: [
          'Smart Sync — one button replaces Sync Now, Collect Now, and Re-seed. Auto-detects first run (90-day seed) vs standard sync (24-hour). Collects users, groups, forensic data, starts EXO/mailbox runbooks, runs full analysis pipeline.',
          'Shared post-collection pipeline — IOC engine, delta engine, findings, alerts, and AI analysis run after every collection (scheduled, manual, or seed).',
        ],
      },
      {
        type: 'fixed',
        items: [
          '"Collect Now" and "Re-seed" now run full analysis pipeline — findings and alerts appear immediately instead of requiring the 2-hour scheduled scan.',
          'Sync status header updates after manual collections.',
          'SOC dashboard auto-refreshes after sync with detailed results message.',
          'Removed duplicate sync buttons — single "Sync Now" in page header.',
          'All sync buttons consistently labeled "Sync Now".',
        ],
      },
    ],
  },
  {
    version: '2.69.0',
    date: '2026-03-24',
    sections: [
      {
        type: 'added',
        items: [
          'Post-collection pipeline shared across all collection flows (scheduled, seed, manual).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Manual collections (Collect Now, Re-seed) now run IOC + delta + findings + alerts.',
          'Delta findings auto-saved — removed manual "Save to SOC" step.',
        ],
      },
    ],
  },
  {
    version: '2.68.0',
    date: '2026-03-24',
    sections: [
      {
        type: 'added',
        items: [
          'Delta Detection Engine — compares each 2-hour scan to the previous run. Detects: failed login spikes, new country logins, inbox rule changes, role added/removed, MFA disabled, mass file downloads, password resets, and more.',
          'Roles column in Tenant Users table — shows directory roles from cached scan data.',
          'Architecture plan for collect-once-serve-from-cache model.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Admin discovery reads from cached Cosmos data (instant, zero API calls).',
          'Report TOC restructured — 1 section per user with subsections.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Investigation findings scoped to target users only.',
          'Map dot counts show correct totals.',
          'Security snapshot 401 in CloudOps HUD (embed mode).',
        ],
      },
    ],
  },
  {
    version: '2.66.0',
    date: '2026-03-24',
    sections: [
      {
        type: 'added',
        items: [
          'Findings UX redesign — clickable severity filter cards, findings grouped by user in collapsible accordions. Click severity to filter, click user to expand.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Admin audit report now includes ALL discovered admin users even when cached forensic data is incomplete.',
        ],
      },
    ],
  },
  {
    version: '2.65.3',
    date: '2026-03-23',
    sections: [
      {
        type: 'added',
        items: [
          'Real world map — Natural Earth country boundaries from GeoJSON, base64-encoded SVG with IP dots overlaid.',
          'All sections collapsible — accordion-style, TOC links auto-expand target.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'All 10 admin users shown using discovery data. Foreign IPs flagged correctly. US IPs no longer falsely flagged.',
        ],
      },
    ],
  },
  {
    version: '2.63.0',
    date: '2026-03-23',
    sections: [
      {
        type: 'added',
        items: [
          'Admin Access Audit — new investigation template for privileged account hygiene. Flags vanity domain admins (not *.onmicrosoft.com), admins without MFA, shared/service accounts with admin roles, foreign sign-ins, and role change history.',
          'Admin Privilege Audit section appears in any investigation with privileged role findings (not just admin_audit template).',
        ],
      },
    ],
  },
  {
    version: '2.62.0',
    date: '2026-03-23',
    sections: [
      {
        type: 'added',
        items: [
          'Interactive Forensic Reports — sortable tables (click column headers), default filters (hide routine FileAccessed/PageViewed/Success rows), IP Triage panel (cycle IPs between suspicious/verified/neutral), world map with geo-plotted IP dots (hover tooltip, click to scroll), location columns on all 7 IP tables.',
          'IP Geolocation Enrichment — report endpoint batch-geolocates all unique IPs from forensic data via ip-api.com cache before generating HTML.',
        ],
      },
      {
        type: 'backend',
        items: [
          'GetInvestigationReport collects all unique IPs from forensic data and passes geo map to report generator (API 1.31.2).',
        ],
      },
    ],
  },
  {
    version: '2.61.4',
    date: '2026-03-18',
    sections: [
      {
        type: 'fixed',
        items: [
          'Email Health column headers (EF, IPs, MX, Connector) now show descriptive tooltips on hover. Dotted underline hints that more info is available.',
        ],
      },
    ],
  },
  {
    version: '2.61.3',
    date: '2026-03-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Scheduled forensic collection was timing out — v2.60.0 increased sign-in collection 10x causing the 2-hour timer to silently die. Scheduled scans now use 100/user; investigations keep 500. Timeout raised to 10 min.',
        ],
      },
    ],
  },
  {
    version: '2.61.2',
    date: '2026-03-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Avatar button shows user icon instead of "?" when name unavailable (looked like help button).',
        ],
      },
    ],
  },
  {
    version: '2.61.1',
    date: '2026-03-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Added "Security Collection" link to avatar dropdown menu (was missing after Settings moved from sidebar in v2.38.0).',
        ],
      },
    ],
  },
  {
    version: '2.61.0',
    date: '2026-03-17',
    sections: [
      {
        type: 'added',
        items: [
          'Nightly EXO Collection — staggered Exchange Online data collection for all clients during configurable window (default 7PM-4AM PT). 1 client per 10-min tick, max 3 concurrent runbooks.',
          'Compromise-Triggered Immediate EXO — 2-hour Graph scan auto-triggers EXO when compromise indicators detected. Separate toggle, off by default.',
          'EXO Collection Monitor — polls pending jobs every 2 min, merges results, re-runs IOC + AI analysis.',
          'Seed Collection — "Seed 90-Day History" button on SOC tab for new client onboarding.',
          'Collection Coverage Display — data range, sources, and seed button on client SOC tab.',
          'Settings > Security Collection — toggles for nightly collection and compromise trigger, configurable nightly window.',
          'Investigations pull from DB — instant results when nightly data covers the date range.',
        ],
      },
      {
        type: 'backend',
        items: [
          'Shared exoMerge.ts module extracted from InvestigationCollectionMonitor.',
          '5 new API endpoints: ScheduledEXOCollection, EXOCollectionMonitor, TriggerSeedCollection, GetCollectionCoverage, EXOCollectionSettings.',
          'New Cosmos types: PendingEXOJob, NightlyEXOBatch, EXOCollectionSettings.',
        ],
      },
    ],
  },
  {
    version: '2.60.0',
    date: '2026-03-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Forensic collection 2MB limit eliminated — split into header + per-user Cosmos docs, reassembled transparently at read time. No more data trimming.',
          'Full sign-in log data restored — 500/user (was 50), full Graph API fields preserved.',
          'Backward compatible — old monolithic docs returned as-is, expire via 90-day TTL.',
        ],
      },
    ],
  },
  {
    version: '2.59.3',
    date: '2026-03-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Forensic collection for large tenants (42+ users) — aggressive data trimming: 50 sign-ins/user, stripped extra Graph fields, progressive size guard auto-trims if doc approaches 1.8MB.',
        ],
      },
    ],
  },
  {
    version: '2.59.1',
    date: '2026-03-16',
    sections: [
      {
        type: 'fixed',
        items: [
          '"Compromised" cry wolf — now distinguishes Compromised (attacker succeeded) from At Risk (risk factors only) from Clean',
          'Email summary and forensic report no longer say "compromised" when all foreign sign-ins failed and no actual compromise occurred',
          'Investigation detail page now shows actual findings after completion instead of just a timeline of what the system did',
        ],
      },
      {
        type: 'added',
        items: [
          'isCompromiseIndicator() — shared logic classifying findings as compromise indicators vs risk factors',
          'GET /security/investigations/{id}/findings — API endpoint returning findings with severity breakdown and overall compromise status',
          'Findings Summary UI on investigation detail — severity cards, overall status badge, finding cards with type labels and status',
        ],
      },
    ],
  },
  {
    version: '2.59.0',
    date: '2026-03-11',
    sections: [
      {
        type: 'fixed',
        items: [
          '180 duplicate findings per user — deterministic SHA-256 finding IDs replace random UUIDs, same finding always produces same ID across scans',
          '"Mark as Safe" now sticks permanently — dismissed findings are skipped on subsequent scans instead of being recreated with new IDs',
          'False positive alerts stop coming back — alert dedup now considers ALL statuses (not just open), only creates new alert for genuinely new findings',
          'Alert page defaults to "new" filter instead of "all" — resolved/false_positive clutter no longer dominates',
          'ConsolidateAlerts now cleans up ALL duplicate alerts including resolved/false_positive',
        ],
      },
      {
        type: 'added',
        items: [
          'Deterministic finding IDs for all 19 IOC detectors (SHA-256 hash of clientId + type + userId + discriminator)',
          'POST /security/admin/deduplicate-findings — cleanup endpoint for existing duplicate findings (dry-run by default)',
          'getDismissedFindingIds, getExistingAlertForClient, deleteSecurityFinding Cosmos queries for dedup logic',
        ],
      },
    ],
  },
  {
    version: '2.58.4',
    date: '2026-03-11',
    sections: [
      {
        type: 'fixed',
        items: [
          'Affected External Contacts panel now always visible on complete investigations — shows clear explanation when no MessageTrace data available',
        ],
      },
      {
        type: 'added',
        items: [
          'Alert consolidation endpoint — one-off cleanup to merge 200+ duplicate alerts into 1 per client',
        ],
      },
    ],
  },
  {
    version: '2.58.3',
    date: '2026-03-11',
    sections: [
      {
        type: 'fixed',
        items: [
          '200 duplicate alerts per client — scheduled scans now dedup (one open alert per client, merges new findings into existing)',
          'Inbox rule conditions not showing — UI was reading wrong rawData path (nested vs flat camelCase)',
          'Attacker emails missing from email summary — added fallback detection when FromIP doesn\'t match attacker IPs',
          'Affected External Contacts panel empty — added two-strategy approach (strict IP match + fallback for compromised users)',
        ],
      },
      {
        type: 'added',
        items: [
          'Alert deduplication query (getOpenSecurityAlertForClient) for ScheduledForensicCollection and DailyDeepAnalysis',
          'Diagnostic logging for email summary, attacker contacts, and report generation (MessageTrace stats, IP counts)',
        ],
      },
    ],
  },
  {
    version: '2.58.2',
    date: '2026-03-11',
    sections: [
      {
        type: 'fixed',
        items: [
          'Auto-resolve alert when all findings dismissed — marking the only finding as "Safe" now auto-resolves the alert',
          'Email summary empty country list — foreign sign-in locations now correctly read from rawData',
          'Email summary missing attacker email section — IOC engine now infers attacker IPs from foreign sign-in data before analyzing',
          'Affected External Contacts panel now uses dedicated API endpoint (was invisible due to empty Cosmos findings)',
        ],
      },
      {
        type: 'added',
        items: [
          'inferForeignIPs() helper in IOC engine for detecting attacker IPs from foreign sign-in data',
          'GET /security/investigations/{id}/attacker-contacts API endpoint',
        ],
      },
    ],
  },
  {
    version: '2.58.1',
    date: '2026-03-10',
    sections: [
      {
        type: 'changed',
        items: [
          'Security Alerts UX redesign — user-first accordion (expand alert → see users → expand user → see findings), "Mark as Safe" / "Disable Inbox Rule" labels, human-readable IOC names, larger text',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Client email summary attacker email section was silently broken (JSON.parse on plain string) — now reads rawData correctly',
        ],
      },
      {
        type: 'added',
        items: [
          'Attacker email detail table in client summary email — external recipients with subject, date, and sender',
          'Notification template in client summary — pre-written copy-pasteable letter for notifying affected external recipients',
          'Affected External Contacts panel on investigation detail — collapsible table with "Copy Notification" per recipient',
        ],
      },
    ],
  },
  {
    version: '2.58.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          '7 new IOC detectors: privileged admin roles, admin role changes, CA policy tampering, suspicious MFA registration, mailbox delegation changes, license abuse, rogue app registrations',
          'Directory Roles data source — forensic collector now fetches admin role assignments per user',
          'Expanded audit activity filter (25 activity types, was 9): role changes, CA policy, app consent, license changes, mailbox delegation',
          'Client email summary: new sections for Privileged Access, Security Config Changes, MFA Security',
          'Investigation templates updated with directoryRoles and all 7 new IOC categories',
        ],
      },
    ],
  },
  {
    version: '2.57.1',
    date: '2026-03-11',
    sections: [
      {
        type: 'fixed',
        items: [
          'Email summary was showing "LOW RISK / no issues" for investigations with real findings — now runs IOC engine inline on forensic data (same as report generator) instead of relying on Cosmos findings container',
        ],
      },
    ],
  },
  {
    version: '2.57.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'Report row limits removed — all forensic data tables now show complete data (was capped at 50-100 rows per section)',
        ],
      },
      {
        type: 'added',
        items: [
          '"Email Summary" button — sends a plain-English investigation summary to non-technical clients (what happened, who was affected, recommendations)',
          'Template comparison matrix — side-by-side feature comparison of all 6 investigation templates in the wizard',
        ],
      },
    ],
  },
  {
    version: '2.56.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'Investigation EXO collection — runbook no longer polled inside Azure Function (was killed by timeout, leaving investigations stuck forever). New timer-based architecture tracks running jobs independently.',
        ],
      },
      {
        type: 'added',
        items: [
          '"Mark Complete" button on stuck investigations — appears after 5 min, lets you generate a report with data collected so far',
          'Auto-complete for stuck investigations — timer auto-completes after 30 min (no EXO job) or 1 hour (EXO running)',
          'InvestigationCollectionMonitor — background timer checks EXO runbook status every 2 min and merges results automatically',
        ],
      },
    ],
  },
  {
    version: '2.55.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          'Manual review workflow — per-finding "Normal Rule" and "Disable Rule" buttons, per-user "Quarantine" button for manual decisions',
          'Full rule detail — shows conditions (when subject/from/body contains...) and all actions (forward, move, delete, mark read)',
          'Disable inbox rules directly from alert view via Graph API',
        ],
      },
      {
        type: 'changed',
        items: [
          'Removed Acknowledge/Investigate/Resolve/False Positive buttons — replaced with simpler "All Normal" and "Resolve"',
        ],
      },
    ],
  },
  {
    version: '2.54.1',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'Auto-quarantine disabled (kill switch) — scheduled scans no longer block user accounts. Requires AUTO_QUARANTINE_ENABLED env var to re-enable.',
          'BEC keyword false positives — words like "bank", "payment", "invoice" in rule subjects no longer create findings alone. Only amplify when other real indicators present.',
          'Severity downgrade — non-forwarding/non-delete inbox rules now "warning" instead of "critical" (never trigger quarantine)',
        ],
      },
      {
        type: 'added',
        items: [
          'Bulk release endpoint — emergency mass unblock of all quarantined users across all clients',
        ],
      },
    ],
  },
  {
    version: '2.54.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'IOC false positive reduction — only flags forwarding to free email providers (Gmail, Yahoo, etc.) and suspicious folder moves when combined with hiding behavior. Legitimate rules no longer flagged.',
        ],
      },
      {
        type: 'added',
        items: [
          'Quarantine Release UI — per-user release buttons with confirmation, "Release All" for bulk release, real-time status badges in expanded alert view',
          'Findings Detail in Alerts — expanding an alert shows actual inbox rules, forwarding targets, detection reasons, grouped by user',
        ],
      },
    ],
  },
  {
    version: '2.53.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          'Investigate All Licensed Users — toggle in Investigation Wizard auto-discovers all licensed users in a tenant (up to 50)',
          'Email notifications for security alerts — configurable recipients, severity threshold, and enable toggle on Alerts Console page',
          '"Notify me when complete" option on Investigation Wizard — receive email when investigation finishes',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Report TOC links — clicking Table of Contents links no longer navigates to blank page, now uses smooth scrolling within the report iframe',
        ],
      },
    ],
  },
  {
    version: '2.52.2',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          'Inline IOC detection — forensic reports auto-detect 8 indicator categories: suspicious inbox rules, foreign sign-ins, attacker-sent emails, credential stuffing, account lockout, anonymous link sharing, missing MFA, and external forwarding',
        ],
      },
    ],
  },
  {
    version: '2.52.1',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'EXO runbook output parsing — regex crossed newline boundaries causing all 7 forensic phases to fail JSON.parse. Published Invoke-ForensicCollect runbook to Azure Automation.',
        ],
      },
    ],
  },
  {
    version: '2.52.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          'Full forensic investigation pipeline — EXO runbook auto-collects hidden inbox rules, UAL auth events, rule changes, SP/OneDrive, message trace, password/MFA events during investigations',
          'Comprehensive HTML forensic report — 401kfe-style professional report with executive summary, IOC analysis, cross-user IP correlation, per-user sign-in/auth/rules/SPO/message trace sections, print styles',
        ],
      },
    ],
  },
  {
    version: '2.51.0',
    date: '2026-03-10',
    sections: [
      {
        type: 'added',
        items: [
          'Inbox rule detection via Graph API — investigations and automated 2-hour scans now detect suspicious rules (RSS Feeds moves, free email forwarding, BEC keywords, obfuscated names)',
          'RSS Feeds added to suspicious folder detection list',
        ],
      },
    ],
  },
  {
    version: '2.50.3',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'Root cause fix for ALL security route 401 errors — changed security/{clientId} wildcard to security/client/{clientId} so it stops swallowing other security/* routes',
        ],
      },
    ],
  },
  {
    version: '2.50.2',
    date: '2026-03-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'Investigations page 401 error — security/{clientId} wildcard route was intercepting the investigations list request',
        ],
      },
    ],
  },
  {
    version: '2.50.1',
    date: '2026-03-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'Clients page Last Sync, Email Health, Security Scan timestamps showing "Never" — route conflict with clients/{id} wildcard',
        ],
      },
      {
        type: 'added',
        items: [
          'Email Health table: column sort (click), multi-sort (Shift+click), per-column filters (text and dropdown), pagination',
          'Summary cards act as quick-filters for status column',
        ],
      },
    ],
  },
  {
    version: '2.50.0',
    date: '2026-03-06',
    sections: [
      {
        type: 'added',
        items: [
          'Run All now persists across page navigation — checks keep running in the background when you leave the Email Health page',
          'Floating progress indicator in bottom-right corner on all other pages showing current client and X/Y progress (click to jump back)',
        ],
      },
    ],
  },
  {
    version: '2.49.3',
    date: '2026-03-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'Run All did nothing on first use — was looping over empty results instead of fetching all clients. Now checks every client sequentially.',
          'Added progress bar during Run All showing current client name and X/Y count',
        ],
      },
    ],
  },
  {
    version: '2.49.2',
    date: '2026-03-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'Remediation guides now show actual SpamHero known-good IPs from settings (no more placeholder text)',
          'Inbound Connector "How to Fix" instructions corrected to match real Exchange Admin Center UI flow',
          'Enhanced Filtering "How to Fix" instructions corrected to match real Microsoft Defender UI',
        ],
      },
    ],
  },
  {
    version: '2.49.1',
    date: '2026-03-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'SpamHero settings not loading — route conflict with /email-health/{clientId?} wildcard was swallowing the settings GET request, so saved known-good IPs appeared empty',
        ],
      },
    ],
  },
  {
    version: '2.49.0',
    date: '2026-03-06',
    sections: [
      {
        type: 'added',
        items: [
          'Email Health Remediation Guides — collapsible "How to Fix" step-by-step instructions on failed/warning check cards (MX Routing, Inbound Connector, Enhanced Filtering, IP Match)',
          'Clients table: Pax8 ID column with Has/Missing filter (moved from Company Name subtitle)',
          'Clients table: Last Sync column — relative timestamp with orange stale indicator (>24h), Recent/Stale/Never filter',
          'Clients table: Email Health column — relative timestamp of last SpamHero check (>48h stale), sortable and filterable',
          'Clients table: Security Scan column — relative timestamp of last forensic collection (>4h stale), sortable and filterable',
          'New GET /clients/check-timestamps API endpoint — parallel bulk query across 3 Cosmos containers',
        ],
      },
    ],
  },
  {
    version: '2.48.0',
    date: '2026-03-06',
    sections: [
      {
        type: 'added',
        items: [
          'SpamHero Connector Health Check — automated daily detection of misconfigured SpamHero inbound connectors across all client tenants',
          'Email Health Dashboard (/email-health) with summary cards, filter tabs, and all-clients table',
          'Client Detail "Email Health" tab with 2x2 check card grid, status banner, and check history',
          'Two-phase check: DNS MX lookup first (instant), then Exchange Online runbook only for SpamHero tenants',
          '4 checks per tenant: MX routing, inbound connector, Enhanced Filtering, IP match against known-good SpamHero IPs',
          'Settings panel for managing known-good SpamHero IP addresses',
          'Scheduled daily check at 2am UTC with batched processing (5 clients at a time, 10s delay)',
          '5 new API endpoints + new Cosmos DB container for check history',
        ],
      },
    ],
  },
  {
    version: '2.47.0',
    date: '2026-02-23',
    sections: [
      {
        type: 'added',
        items: [
          'Security Operations Center Phases 2-6 — complete enterprise SOC implementation',
          'Phase 2: EXO forensic collection via Azure Automation runbook (7 phases: InboxRules, Forwarding, AuthAudit, RuleChanges, SPOneDrive, MessageTrace, PasswordMFA)',
          'Phase 3: IOC Detection Engine with 12 categories (suspicious inbox rules, foreign sign-ins, legacy auth, risky sign-ins, external forwarding, no MFA, password resets, known attacker IPs, attacker-sent emails, OAuth consent, mass rule cleanup, cross-user IP correlation)',
          'Phase 3: Threat Intelligence integration — AbuseIPDB, Feodo Tracker, ThreatFox, HIBP with Cosmos DB caching',
          'Phase 4: AI analysis with vLLM/Claude failover and PII sanitization for cloud calls',
          'Phase 4: Auto-quarantine with safety rails (rate limiting, IP allowlist, admin protection)',
          'Phase 4: Scheduled collection (2-hour timer) + daily deep AI analysis (3am)',
          'Phase 5: Bloomberg terminal-style SOC Dashboard with LIVE indicator, 24h activity charts, IOC breakdown, live alert feed',
          'Phase 5: Security Alerts console with severity/status filters, expandable details, action buttons',
          'Phase 5: Threat Map with IP geolocation, sortable threat locations table',
          'Phase 5: Client-level SOC tab with security summary, alerts, and findings',
          'Phase 6: Investigation Wizard (4-step workflow, 6 templates: BEC, Account Takeover, Phishing, Insider Threat, Post-Breach, Health Check)',
          'Phase 6: Investigation Detail with real-time auto-polling, timeline visualization',
          'Phase 6: Forensic Report Generator with dark-theme HTML reports',
          'Phase 6: 5 new API endpoints for investigation CRUD and report generation',
        ],
      },
    ],
  },
  {
    version: '2.46.0',
    date: '2026-02-24',
    sections: [
      {
        type: 'added',
        items: [
          'Security Operations Center Phase 1 — enterprise forensic data collection and monitoring foundation',
          'Graph API forensic collectors: sign-in logs, risk detections, directory audits, auth methods, MFA registration',
          'IP Geolocation service with batch lookups, Cosmos DB cache, and IP cleaning',
          '6 new Cosmos DB containers for forensic data, findings, alerts, investigations, geolocation, threat intel',
          '7 new API endpoints for security data collection, querying, and alert management',
          'Complete TypeScript type system for all forensic and security data structures',
        ],
      },
    ],
  },
  {
    version: '2.45.0',
    date: '2026-02-20',
    sections: [
      {
        type: 'added',
        items: [
          'SharePoint Site Permission Manager — new "SharePoint" tab on Client Detail to view sites and manage Owners/Members/Visitors',
          'Sites load via Graph API (fast); permissions managed via PnP PowerShell runbooks',
          'Add/remove members from any SharePoint site group with audit logging',
        ],
      },
    ],
  },
  {
    version: '2.44.1',
    date: '2026-02-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'Removed broken "Open Approval Window" button — MS admin portal ignores external account picker params',
          'Fixed Copy URL button with clipboard fallback for all browsers',
        ],
      },
    ],
  },
  {
    version: '2.44.0',
    date: '2026-02-19',
    sections: [
      {
        type: 'added',
        items: [
          'GDAP Onboarding auto-populates client info when launched from Client Detail page — skips step 1',
          'Approval window uses MS account picker (prompt=select_account) so you can sign in as the customer',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Back button in onboarding wizard returns to client page when launched from Client Detail',
        ],
      },
    ],
  },
  {
    version: '2.43.1',
    date: '2026-02-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'GDAP "Already Exists" recovery: wizard now finds existing relationship and lets you continue instead of showing error',
        ],
      },
    ],
  },
  {
    version: '2.43.0',
    date: '2026-02-19',
    sections: [
      {
        type: 'added',
        items: [
          'GDAP Relationship Status card on Client Detail sidebar — shows Active/Pending/Expired/None with onboarding button',
        ],
      },
      {
        type: 'fixed',
        items: [
          'GDAP Approval instructions updated with customer name and InPrivate/Incognito tip',
        ],
      },
    ],
  },
  {
    version: '2.42.0',
    date: '2026-02-17',
    sections: [
      {
        type: 'added',
        items: [
          'TAM Tenant ID Sync: Sync missing tenant IDs from Tenant Access Manager (TAM) with multi-strategy matching',
          'New "Sync from TAM" button on Tenant Matcher page with detailed results and match breakdown',
          'TAM API client module with API key authentication',
        ],
      },
    ],
  },
  {
    version: '2.41.0',
    date: '2026-02-17',
    sections: [
      {
        type: 'added',
        items: [
          'Change Email Address: Change a user\'s email/UPN and domain from User Detail with verified domain dropdown',
          'New API endpoint for updating user email with friendly error messages',
        ],
      },
    ],
  },
  {
    version: '2.40.0',
    date: '2026-02-16 5:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Quick TAP: One-click TAP generation that shows the code on screen — ideal for reading to a user over the phone',
          'Advanced TAP: Configurable duration (1-24h), one-time or multi-use toggle, optional delayed start',
          'Two delivery modes: "Show on Screen" with copy-to-clipboard, or "Send to User" via email/SMS',
          'New API endpoint for generating TAP codes that return the code in the response',
          'TAP policy defaults updated: 2hr default, 10min minimum, multi-use policy support',
        ],
      },
    ],
  },
  {
    version: '2.39.2',
    date: '2026-02-16 2:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Migrated all 52 page components from hardcoded gray/dark color classes to design tokens (text-muted-foreground, bg-card, bg-muted, border-border, hover:bg-accent)',
          'Consistent theming across light/dark/system modes on every page',
        ],
      },
    ],
  },
  {
    version: '2.39.1',
    date: '2026-02-16 10:00 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Primary/secondary CSS variable defaults in Tailwind config (bg-primary, text-primary-foreground now work correctly)',
          'Collapsed sidebar tooltips use Radix portal (no longer clipped by ScrollArea overflow)',
          'Breadcrumb HTML nesting (separator was nested inside item, both rendering <li>)',
          'Double animation delay on dashboard stat cards removed',
          'Warning confirm dialog button now renders amber instead of default blue',
          'QuickSetupWizard RingCentral status check fixed (useState → useEffect)',
          'Groups tab uses distinct icon (UsersRound) instead of same Users icon',
          'Added DialogDescription to 9 dialogs for accessibility',
          'Desktop sidebar z-index, skeleton React import, count-up animation entry',
        ],
      },
    ],
  },
  {
    version: '2.39.0',
    date: '2026-02-14 3:30 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Enterprise Redesign: Complete UI overhaul — shadcn/ui (Radix UI) + Lucide React replaces Headless UI + Heroicons',
          'Collapsible sidebar (280px → 70px icon rail), glassmorphism header with breadcrumbs, Cmd+K command palette',
          'Dashboard: bento grid layout, animated counters, skeleton loading states',
          'HSL design tokens, Geist font, 17 new shadcn/ui components, dark/light/system theme toggle',
          'Removed @headlessui/react (12 files migrated) and @heroicons/react (57 files migrated)',
        ],
      },
    ],
  },
  {
    version: '2.38.2',
    date: '2026-02-14 12:00 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'RingCentral OAuth opens in new tab when in CloudOps HUD iframe (fixes "content is blocked" error)',
          'RingCentral OAuth callback redirects to correct settings page (success/error messages were lost)',
        ],
      },
    ],
  },
  {
    version: '2.38.0',
    date: '2026-02-13 11:00 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Settings moved from sidebar to user avatar dropdown menu (top right)',
          'Settings split into "My Settings" (RingCentral) and "System Settings" (SMTP) with tabbed navigation',
        ],
      },
    ],
  },
  {
    version: '2.37.3',
    date: '2026-02-13 10:30 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Send Secure Link: Phone field shows RingCentral status — green "SMS Ready" or yellow warning to connect RC in Settings first. Falls back to email verification if RC not set up.',
        ],
      },
    ],
  },
  {
    version: '2.37.2',
    date: '2026-02-13 10:25 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Send Secure Link: Phone number input field no longer disabled (was blocked by RingCentral status check)',
        ],
      },
    ],
  },
  {
    version: '2.37.1',
    date: '2026-02-13 10:00 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Send Secure Link: Phone number field now visible in Send Secure Link section (was hidden in wrong dialog)',
          'Send Secure Link: Secure credential generated at send time with phone number (was generated too early before phone entered)',
          'Send Secure Link: SMS verification code sent to phone when provided (not to locked email)',
        ],
      },
    ],
  },
  {
    version: '2.37.0',
    date: '2026-02-13 9:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Secure Credentials SMS Verification: Verification code sent via SMS when phone number provided — solves the problem of users not being able to access email to verify',
          'Password Reset: Phone number field shown before reset when RingCentral connected',
        ],
      },
    ],
  },
  {
    version: '2.36.0',
    date: '2026-02-13 8:00 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Password Reset: Secure credential delivery — one-time link with email verification, self-destruct after viewing (matches Create User flow)',
          'Password Reset: Fixed "Send Email" resetting password a second time; Copy to Clipboard now works correctly',
        ],
      },
    ],
  },
  {
    version: '2.35.4',
    date: '2026-02-13 6:40 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Password Reset: Fixed SP lookup to use actual AZURE_CLIENT_ID (was hardcoded), added propagation delay, diagnostic error messages',
        ],
      },
    ],
  },
  {
    version: '2.35.2',
    date: '2026-02-13 5:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Password Reset: Now uses auth methods API (same as MFA reset) — fixes "Insufficient privileges" error with GDAP customers',
          'Clients Sort: Trims whitespace from company names, removed stale useMemo caching',
        ],
      },
    ],
  },
  {
    version: '2.35.1',
    date: '2026-02-13 4:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Clients Page: Multi-sort with shift+click — numbered order badges and ▲/▼/○ sort indicators',
          'Clients Page: Inline column filters — text filter with autocomplete (Company Name), dropdown filters (Status, Payment, Tenant ID)',
          'Clients Page: Missing tenant IDs highlighted in red with warning icon',
        ],
      },
      {
        type: 'changed',
        items: [
          'Clients Page: Removed separate filter panel — filters now built into column headers',
          'Clients Page: All clients loaded at once (pageSize=500) for instant client-side filtering',
        ],
      },
    ],
  },
  {
    version: '2.35.0',
    date: '2026-02-13 3:30 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Quick Setup: One-click user onboarding — enable account, assign license, reset password, and send credentials all in one action',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Modal Refresh: All user actions now properly refresh data without closing/reopening the modal',
          'Password Reset: "Insufficient privileges" error now shows actionable guidance (GDAP consent, admin roles)',
        ],
      },
    ],
  },
  {
    version: '2.34.8',
    date: '2026-02-13 3:05 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'License Assignment: Proactively checks and sets usageLocation to US before assigning license (not just on error retry)',
        ],
      },
    ],
  },
  {
    version: '2.34.7',
    date: '2026-02-13 3:00 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'License Assignment: Auto-sets usage location to US when missing (fixes "invalid usage location" error for users created without a location)',
        ],
      },
    ],
  },
  {
    version: '2.34.6',
    date: '2026-02-13 2:45 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Users Tab: Shows ALL tenant users including disabled accounts (was hiding shared mailboxes and disabled users)',
        ],
      },
    ],
  },
  {
    version: '2.34.5',
    date: '2026-02-13 2:30 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Create User: Auto-detects duplicate UPN in active AND deleted users, one-click "Purge & Retry" for recycle bin conflicts',
        ],
      },
    ],
  },
  {
    version: '2.34.4',
    date: '2026-02-13 2:15 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Create User: Enhanced error messages with actionable guidance (duplicate UPN, password complexity, domain issues)',
        ],
      },
    ],
  },
  {
    version: '2.34.3',
    date: '2026-02-13 2:00 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Create User: License dropdown now fetches live MS Graph data (not stale Cosmos records)',
        ],
      },
    ],
  },
  {
    version: '2.34.2',
    date: '2026-02-13 1:45 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Create User: Uses most recently synced sub per SKU for accurate availability',
          'GDAP Sync: Also updates Direct subs when Pax8 match found (prevents stale data)',
        ],
      },
    ],
  },
  {
    version: '2.34.1',
    date: '2026-02-13 1:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Create User: "No available licenses" when licenses ARE available — prefers MS Direct subs for accurate availability data',
        ],
      },
    ],
  },
  {
    version: '2.34.0',
    date: '2026-02-13 12:45 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Clients Page: "X wasted" count now uses MS Direct assigned counts (was reading null from Pax8 subs)',
          'Alert Generation: Pax8 utilization alerts use MS Direct assigned count instead of null=0',
          'License Report: Copy to clipboard in iframe/HUD shows selectable textarea fallback',
          'License Report: Uses MS Direct quantity as tenant truth — no double-counting',
        ],
      },
    ],
  },
  {
    version: '2.33.5',
    date: '2026-02-13 11:30 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'License Report: Groups by normalized product name (fixes M365 Business Standard duplication from MS SKU aliases)',
          'License Report: MS Direct-only subs flagged with "MS Direct — Migrate to Pax8" badge',
          'License Report: Free licenses only shown when users assigned, no unassigned count for free',
          'License Report: Paid sorted by total quantity desc, unassigned-only at bottom of paid, free at end',
          'License Report: All active subs with quantity > 0 now show regardless of source',
        ],
      },
    ],
  },
  {
    version: '2.33.4',
    date: '2026-02-13 10:45 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'License Report: No longer duplicates licenses (was showing 3x for Exchange, M365, etc.)',
          'License Report: Paid licenses sorted first, free/trial licenses at bottom',
          'License Report: Shows unassigned paid licenses like Visio without creating duplicates',
          'License Report: POWERAPPS_DEV → Power Apps (Dev) friendly name',
          '"Order from Pax8" no longer shows false warnings for products already covered by Pax8',
        ],
      },
    ],
  },
  {
    version: '2.33.1',
    date: '2026-02-11 12:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'License Report: By License view now shows unused license counts with orange badges',
          '"Show only licenses with unused seats" filter checkbox in By License view',
          'Total unused count shown in stats bar',
          'Unused counts included in copied text and emailed reports',
        ],
      },
    ],
  },
  {
    version: '2.33.0',
    date: '2026-02-11 11:30 AM',
    sections: [
      {
        type: 'added',
        items: [
          'Email Authentication DNS Check: One-click SPF/DKIM/DMARC verification for client domains',
          'Available from Message Trace page ("Check Email Auth") and Client Detail page ("Email Auth")',
          'Color-coded status: green (pass), yellow (warning/monitoring), red (missing)',
          'Expandable domain cards show actual DNS records and remediation guidance',
          'DMARC policy interpretation: reject/quarantine = enforced (green), none = monitoring (yellow)',
          'Uses fast DNS lookups (2-3 seconds, no runbook needed)',
        ],
      },
    ],
  },
  {
    version: '2.32.0',
    date: '2026-02-11 10:00 AM',
    sections: [
      {
        type: 'added',
        items: [
          'Message Trace: Trace email delivery through Exchange Online for any client',
          'Search by sender, recipient, date range, delivery status, or Message ID',
          'Color-coded status badges: Delivered (green), Failed (red), Pending (yellow), Quarantined (orange)',
          'Click any message to see detailed routing events with visual timeline',
          'Quick-link button on Client Detail page pre-selects the client',
          'New sidebar navigation link under Audit Log',
        ],
      },
    ],
  },
  {
    version: '2.31.4',
    date: '2026-02-09 11:20 AM',
    sections: [
      {
        type: 'added',
        items: [
          'Sync History: Click on "X errors" badge to expand and see actual error messages',
          'Expandable row shows all error details in a list',
        ],
      },
    ],
  },
  {
    version: '2.31.3',
    date: '2026-02-09 11:10 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Distribution group members now display correctly after adding',
          'Graph API doesnt return dist list members - now uses Exchange Online runbook',
          'Created Get-DistributionGroupMembers runbook for fetching members',
        ],
      },
    ],
  },
  {
    version: '2.31.2',
    date: '2026-02-09 11:00 AM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Runbook auth: Fixed "Access Denied" when adding distribution group members',
          'Now uses Automation Variables with PFX certificate (matches other Exchange runbooks)',
        ],
      },
    ],
  },
  {
    version: '2.31.1',
    date: '2026-02-09 10:40 AM',
    sections: [
      {
        type: 'added',
        items: [
          'Multi-select for Distribution Groups: Select multiple users with checkboxes, add all at once',
          'Progress bar shows Exchange Online operation status (X of Y users)',
          'Select All / Deselect All option for bulk selection',
          'Summary toast shows success/failure counts after completion',
          'Member list auto-refreshes after adding members',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Distribution group member management now uses Exchange Online runbook (Graph API limitation)',
          'Created Manage-DistributionGroupMember Azure Automation runbook',
        ],
      },
    ],
  },
  {
    version: '2.30.8',
    date: '2026-02-06 5:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Reset & Re-consent: Button now deletes service principal before re-consenting',
          'Forces fresh consent with ALL current permissions including new ones',
          'Fixes issue where re-consenting did not pick up newly added permissions',
        ],
      },
    ],
  },
  {
    version: '2.30.7',
    date: '2026-02-06 4:45 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'Clients Page: Live search - results filter as you type (300ms debounce)',
          'No longer need to press Enter to search',
        ],
      },
    ],
  },
  {
    version: '2.30.6',
    date: '2026-02-06 4:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'ConsentWizard: Was showing "Done" even when GroupMember.ReadWrite.All permission was missing',
          'Now correctly checks groupMemberPermission in addition to passwordReset and tapPolicy',
        ],
      },
    ],
  },
  {
    version: '2.30.5',
    date: '2026-02-06 4:00 PM',
    sections: [
      {
        type: 'changed',
        items: [
          'GDAP Consent: Shows orange "Needs Update" when GroupMember.ReadWrite.All permission is missing',
          'App Consent step now checks for password reset, TAP policy, AND group member permissions',
          '"Grant Missing Consents" button appears until ALL permissions are granted',
        ],
      },
    ],
  },
  {
    version: '2.30.4',
    date: '2026-02-06 3:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Group membership operations: Added GroupMember.ReadWrite.All permission to app registration',
          'Root cause: GDAP gives admin roles to users, but app uses application credentials',
          'Existing clients need to re-consent for new permission to take effect',
          'How to fix: GDAP Consent → Re-consent App, or have customer visit admin consent URL',
        ],
      },
    ],
  },
  {
    version: '2.30.0',
    date: '2026-02-06',
    sections: [
      {
        type: 'added',
        items: [
          'Onboarding: Can now select an existing Pax8 client to create upgraded GDAP',
          'New/Existing toggle in Step 1 of onboarding wizard',
          'Searchable client list when "Existing Client" is selected',
          'Creates full 26-role GDAP including Groups Administrator (Pax8 GDAP has limited roles)',
        ],
      },
    ],
  },
  {
    version: '2.29.3',
    date: '2026-02-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'Client Onboarding route: Added to embed mode routes (was only in authenticated routes)',
          'Hooks now skip API calls for reserved route names (onboard, new, create, edit)',
          'Prevents 404 errors when navigating to /clients/onboard',
        ],
      },
    ],
  },
  {
    version: '2.29.0',
    date: '2026-02-05 12:00 AM',
    sections: [
      {
        type: 'added',
        items: [
          'Group email addresses now shown in Groups tab list and when clicking on a group',
          'Edit group membership: Add or remove members directly from the group modal',
          'Click "Add Member" to search and add users to a group',
          'Hover over a member and click the trash icon to remove them',
        ],
      },
    ],
  },
  {
    version: '2.28.5',
    date: '2026-02-04 11:30 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Diagnostic endpoint to debug msSkuId matching issues',
          'Shows all Direct subscriptions and MS licenses for a client',
          'Displays all matching attempts with match type (exact_sku, normalized_sku, etc.)',
          'Add ?fix=true to force-fix missing msSkuIds',
          'Use: GET /api/clients/{clientId}/diagnose-sku',
        ],
      },
    ],
  },
  {
    version: '2.28.1',
    date: '2026-02-04 10:30 PM',
    sections: [
      {
        type: 'fixed',
        items: [
          'Bulk Assign button now appears: msSkuId was not being saved when creating Direct subscriptions',
          'Fixed in both SyncGDAPLicenses and TriggerGDAPSync',
          'Run "Sync GDAP" on a client to populate msSkuId for existing subscriptions',
        ],
      },
    ],
  },
  {
    version: '2.28.0',
    date: '2026-02-04 9:00 PM',
    sections: [
      {
        type: 'added',
        items: [
          'Bulk License Assignment: Assign licenses to multiple users at once from License Coverage view',
          'Hover over MS subscription row with available licenses to see "Assign" button',
          'Modal shows all users without that license - search, select all, or pick individually',
          'Progress indicator and rate limiting (200ms delay) prevent API throttling',
          'Warning dialog if selecting more users than available (allows proceeding)',
        ],
      },
    ],
  },
  {
    version: '2.12.1',
    date: '2026-01-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Security: TAP code is no longer displayed on screen after sending',
          'TAP is now only visible in the email sent to the recipient',
          'API no longer returns TAP code in response - only sent via secure email',
        ],
      },
    ],
  },
  {
    version: '2.12.0',
    date: '2026-01-17',
    sections: [
      {
        type: 'added',
        items: [
          'Send TAP to Existing Users: New "Send TAP" button in User Detail > Actions tab',
          'TAP can be sent to alternate email address (useful when user is locked out of M365)',
          'Auto-enables TAP policy in tenant before generating TAP',
        ],
      },
      {
        type: 'backend',
        items: [
          'New API: POST /api/clients/{id}/users/{userId}/send-tap - Generate and email TAP for existing user',
        ],
      },
    ],
  },
  {
    version: '2.11.0',
    date: '2026-01-17 01:15 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Secure Credentials System: Passwords no longer sent in plain email',
          'TAP (Temporary Access Pass) support for Azure AD Premium tenants - single-use codes that force MFA setup',
          'One-time secure link for non-premium tenants - credentials viewable once then deleted',
          'New /credentials/:token page for viewing secure credentials',
          'TAP-eligible license detection (P1, P2, Business Premium, E3, E5)',
        ],
      },
      {
        type: 'backend',
        items: [
          'New API: POST /api/generate-tap - Creates Temporary Access Pass via Graph API',
          'New API: POST /api/secure-credentials - Stores credentials with 24hr TTL',
          'New API: GET /api/secure-credentials/:token - Retrieves and deletes credentials (one-time view)',
          'New API: POST /api/send-tap-email - Professional TAP email template',
          'New API: POST /api/send-secure-link-email - Secure link email template',
        ],
      },
    ],
  },
  {
    version: '2.10.2',
    date: '2026-01-17 00:25 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Credential emails now sent from signed-in user\'s address (e.g., jgoode@umetech.net)',
          'Email footer updated to "Umetech, Inc."',
          'Falls back to configured SMTP from address if user email unavailable',
        ],
      },
    ],
  },
  {
    version: '2.10.1',
    date: '2026-01-16 23:35 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Removed "Open in Email App" option from Create User success modal',
          'mailto: links cannot support HTML formatting - only plain text',
          'Now offers two clean options: Copy to Clipboard and Send Email Directly (SMTP)',
        ],
      },
    ],
  },
  {
    version: '2.10.0',
    date: '2026-01-16 23:20 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Settings Page: Configure SMTP email settings for sending user credentials',
          'Email Options in Create User: Copy to clipboard, Open in email app (mailto:), or Send directly via SMTP',
          'Auto MS Sync: After creating a user with license assignment, MS data automatically refreshes',
          'Email Test Page: Test email sending at /dev/email-test without creating users',
        ],
      },
      {
        type: 'backend',
        items: [
          'Azure Key Vault integration for secure SMTP credential storage',
          'Passwords are encrypted at rest and masked in UI after saving',
          'New API endpoints: GET/POST /api/settings, POST /api/settings/test-smtp',
          'SendCredentialsEmail now reads settings from Key Vault instead of environment variables',
        ],
      },
    ],
  },
  {
    version: '2.9.0',
    date: '2026-01-08 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'SecureScan Integration: Daily M365 security posture collection for all tenants',
          'Collects: Secure Score, MFA coverage, Admin inventory, CA policies, Mailbox security',
          'New API endpoints: GET /api/security, /api/security/{clientId}, /api/security/by-tenant/{tenantId}',
          'Supports API key authentication for external SecureScan integration',
        ],
      },
      {
        type: 'backend',
        items: [
          'New SyncM365Security timer function (daily at 7:00 AM UTC)',
          'New security Cosmos DB container stores snapshots per tenant',
          'Uses existing GDAP auth infrastructure and report generator patterns',
          'Graceful degradation for tenants without Azure AD Premium',
        ],
      },
    ],
  },
  {
    version: '2.8.12',
    date: '2025-12-31 17:55 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Consent status API now correctly returns password reset permission status',
          'Issue: API was returning stale results without passwordResetPermission field',
          'Root cause: Backend API wasn\'t deployed with latest consent detection code',
          'Consent status now correctly shows "3 of 3 complete" when all permissions granted',
        ],
      },
    ],
  },
  {
    version: '2.8.11',
    date: '2025-12-31 15:15 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Consent detection: restarted Function App, fresh API deployment',
          'Permission was granted but API cache was returning stale data',
          'Consent URL now uses v2.0 endpoint with scope parameter',
        ],
      },
    ],
  },
  {
    version: '2.8.10',
    date: '2025-12-31 14:45 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Consent redirect error: "account does not exist in tenant" after granting consent',
          'Added redirect_uri to consent URLs to avoid SWA auth conflict',
          'Consent now completes cleanly without triggering Umetech login',
        ],
      },
    ],
  },
  {
    version: '2.8.9',
    date: '2025-12-31 14:15 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Header "Consents OK" button now matches GDAP Consents card status',
          'Was showing green even when password reset permission was missing',
          'Now checks both allConsentsGranted AND passwordResetPermission',
        ],
      },
    ],
  },
  {
    version: '2.8.8',
    date: '2025-12-31 14:00 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'GDAP Consent UX: Instruction popups before opening consent windows',
          'Clicking "Needs Update" or "Grant Consent" shows guidance first',
          'Instructions explain: which account to sign in with, what to click, next steps',
          'Matches MS Admin Center link pattern for consistency',
          'Follow-up alert reminds user to click "Refresh" after consenting',
        ],
      },
    ],
  },
  {
    version: '2.8.7',
    date: '2025-12-31 13:30 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'License assignment auto-refresh: User list now updates automatically',
          'Added 500ms delay for Graph API propagation before refresh',
          'handleAssignLicense and handleRemoveLicense now properly await data refresh',
        ],
      },
    ],
  },
  {
    version: '2.8.6',
    date: '2025-12-31 13:15 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'GDAP Consent: Simplified from 5 confusing steps to 3 clear steps',
          'Steps: App Consent, Graph CLI, Exchange Role',
          'App Consent shows "Needs Update" (yellow) when password reset permission missing',
          'Clicking "Needs Update" opens re-consent popup directly',
        ],
      },
    ],
  },
  {
    version: '2.8.5',
    date: '2025-12-31 13:00 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'GDAP Consent: "Re-consent App" button for Password Reset step',
          'One-click to open Microsoft admin consent popup',
          'After consenting, refresh to see updated status',
        ],
      },
    ],
  },
  {
    version: '2.8.4',
    date: '2025-12-31 12:45 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'GDAP Consent: Password Reset permission check (UserAuthenticationMethod.ReadWrite.All)',
          'New "5. Password Reset" row in GDAP Consents status card',
          'Shows Done/Missing status for password reset capability',
          'Instructions for re-consenting to enable password reset',
        ],
      },
      {
        type: 'backend',
        items: [
          'GetConsentStatus checks for UserAuthenticationMethod.ReadWrite.All permission',
          'Added passwordResetPermission field to ConsentStatusResult',
        ],
      },
    ],
  },
  {
    version: '2.8.3',
    date: '2025-12-31 11:45 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Create User license dropdown: fixed duplicate licenses and invalid SKU errors',
          'Now filters to only subscriptions with valid MS SKU ID (Pax8 SKUs don\'t work)',
          'Dedupes by msSkuId to prevent showing same license twice',
        ],
      },
    ],
  },
  {
    version: '2.8.2',
    date: '2025-12-31 10:00 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Manual Tenant Matching UI: Link Pax8 clients to Partner Center customers',
          'New "Manual Tenant Matching" section in Activity Log → Sync History',
          'Dropdown to select matching Partner Center customer',
          'One-click "Link" button to assign tenant ID',
        ],
      },
      {
        type: 'backend',
        items: [
          'New GetTenantMatchingData API',
          'New LinkClientTenant API',
          'Partner Center customers stored for offline matching',
        ],
      },
    ],
  },
  {
    version: '2.8.1',
    date: '2025-12-31 08:30 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Partner Center OAuth for Tenant ID Sync',
          'New "Sync Tenant IDs" button in Sync History tab',
          'Automatic fuzzy-matching of company names to Pax8 clients',
          'Shows sync results: updated, skipped, not matched',
        ],
      },
      {
        type: 'backend',
        items: [
          'New GetPartnerCenterAuthUrl API',
          'New PartnerCenterCallback API',
        ],
      },
    ],
  },
  {
    version: '2.8.0',
    date: '2025-12-30',
    sections: [
      {
        type: 'added',
        items: [
          'Automated 4-Hour Sync: Full Pax8 + GDAP sync runs every 4 hours',
          'Manual "Sync Now" button in Activity Log → Sync History',
          'Sync history records for auditing',
        ],
      },
      {
        type: 'backend',
        items: [
          'New ScheduledFullSync Azure Function with timer trigger',
          'New TriggerFullSync API for manual sync',
        ],
      },
    ],
  },
  {
    version: '2.7.1',
    date: '2025-12-30',
    sections: [
      {
        type: 'fixed',
        items: [
          'BillingAuthCallback initialization: Fixed function not registering',
          'Changed to lazy initialization pattern for CosmosClient',
        ],
      },
    ],
  },
  {
    version: '2.7.0',
    date: '2025-12-30',
    sections: [
      {
        type: 'changed',
        items: [
          'GDAP-ToolKit Report Styling: Complete PDF styling overhaul',
          'Table headers now dark blue (#003D82) with white text',
          'Bar charts use gradient fills (Light Blue to UmetechBlue)',
          'Continuation headers with blue gradient background',
          'Status badges for MFA, Risk Level, Enabled/Disabled states',
        ],
      },
    ],
  },
  {
    version: '2.6.6',
    date: '2025-12-30',
    sections: [
      {
        type: 'changed',
        items: [
          'MS Admin Center link: OK copies URL to clipboard for private window',
          'Cancel opens normally for current session',
        ],
      },
    ],
  },
  {
    version: '2.6.5',
    date: '2025-12-30',
    sections: [
      {
        type: 'changed',
        items: [
          'Improved MS Admin Center instructions explaining account switching',
        ],
      },
    ],
  },
  {
    version: '2.6.4',
    date: '2025-12-30',
    sections: [
      {
        type: 'added',
        items: [
          'PDF Professional Pagination: GDAP-Toolkit style formatting',
          'Continuation headers on pages 2+ with logo and page numbers',
          'Running footer on every page with company info',
          'Direct Portal Links: Open Pax8 Portal and MS Admin Center from client detail',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Pax8 portal URL corrected',
          'MS Admin Center link uses ?tid= parameter',
        ],
      },
    ],
  },
  {
    version: '2.5.10',
    date: '2025-12-30',
    sections: [
      {
        type: 'fixed',
        items: [
          'Delete executions API was not registered (multi-select delete)',
          'Dark mode styling for filter dropdowns',
        ],
      },
    ],
  },
  {
    version: '2.5.9',
    date: '2025-12-30',
    sections: [
      {
        type: 'changed',
        items: [
          'Renamed "Scheduled Reports" to "Security Reports"',
          'Converted report definitions to table view with inline filters',
          'Added multi-select delete for report executions',
        ],
      },
    ],
  },
  {
    version: '2.5.8',
    date: '2025-12-30',
    sections: [
      {
        type: 'changed',
        items: [
          'Direct PDF download instead of HTML viewer for reports',
        ],
      },
    ],
  },
  {
    version: '2.5.7',
    date: '2025-12-30',
    sections: [
      {
        type: 'fixed',
        items: [
          'Mobile: tap report row to view (eye icon was hidden)',
        ],
      },
    ],
  },
  {
    version: '2.5.6',
    date: '2025-12-30',
    sections: [
      {
        type: 'fixed',
        items: [
          'License double-counting when Pax8 subscription matches MS Direct',
        ],
      },
    ],
  },
  {
    version: '2.5.5',
    date: '2025-12-29',
    sections: [
      {
        type: 'fixed',
        items: [
          'Success popup now stays open after user creation',
        ],
      },
    ],
  },
  {
    version: '2.5.4',
    date: '2025-12-29',
    sections: [
      {
        type: 'added',
        items: [
          'Create User: Add new users directly from tenant users list',
          'First/Last Name fields with auto-generated Display Name',
          'Domain dropdown populated from tenant\'s verified domains',
          'License assignment option during user creation',
          'Professional success modal with account details and copy button',
          'License counts auto-update when assigning/removing licenses',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Domain dropdown showing wrong tenant domains (now uses customer tenant)',
          'License usage not updating after user creation with license',
        ],
      },
      {
        type: 'backend',
        items: [
          'New GetTenantDomains API for fetching customer domains',
          'New CreateUser API with optional license assignment',
          'License count tracking: increment/decrement on assign/remove',
        ],
      },
    ],
  },
  {
    version: '1.9.7',
    date: '2025-12-29',
    sections: [
      {
        type: 'added',
        items: [
          'Help tooltips on all User Detail action buttons (Reset, Disable, Offboard)',
          'Each tooltip explains what the action does and what it doesn\'t do',
        ],
      },
      {
        type: 'changed',
        items: [
          'Help Center: Updated offboarding docs with complete 6-step wizard',
          'Help Center: Groups article now includes member viewing feature',
          'Offboard wizard: Exchange operations marked as "Automated" (not manual)',
        ],
      },
    ],
  },
  {
    version: '1.9.6',
    date: '2025-12-28',
    sections: [
      {
        type: 'added',
        items: [
          'Group Member Viewing: Click any group to see its members',
          'Member type display (user, group, device, service principal)',
          'Exchange Offboarding Operations via Azure Automation Runbook',
        ],
      },
      {
        type: 'changed',
        items: [
          'Groups tab rows now clickable with hover cursor',
          'Help tooltip added to Group Type column header',
        ],
      },
      {
        type: 'backend',
        items: [
          'New GetGroupMembers API endpoint',
          'Azure Automation Runbook: Offboard-User.ps1 for Exchange operations',
        ],
      },
    ],
  },
  {
    version: '1.9.5',
    date: '2025-12-28',
    sections: [
      {
        type: 'fixed',
        items: [
          'Tooltips now use React Portal to float on top of page (no longer clipped by containers)',
          'Tooltip flickering completely resolved with delay mechanism',
          'Improved tooltip styling: dark background with blue border for visibility',
        ],
      },
    ],
  },
  {
    version: '1.9.4',
    date: '2025-12-27',
    sections: [
      {
        type: 'added',
        items: [
          'Version badge now always visible in header on all screen sizes',
          'More help tooltips on Client Detail (sync buttons, tabs, sections)',
          'Help tooltips on User Detail modal tabs',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Tooltip flickering when hovering - now stable',
        ],
      },
    ],
  },
  {
    version: '1.9.3',
    date: '2025-12-27',
    sections: [
      {
        type: 'added',
        items: [
          'Help Center: Comprehensive searchable help page with 25+ articles',
          '10 categories covering all features',
          'Search functionality with relevance ranking',
          'Help link in sidebar navigation',
        ],
      },
      {
        type: 'changed',
        items: [
          'Help tooltip icons changed from gray to blue for visibility',
          'Enhanced version badge in sidebar with blue styling',
        ],
      },
    ],
  },
  {
    version: '1.9.2',
    date: '2025-12-27',
    sections: [
      {
        type: 'added',
        items: [
          'Comprehensive help tooltips (?) throughout the app',
          'Version in header dropdown menu for mobile visibility',
          'PWA support - app can be installed on mobile devices',
        ],
      },
    ],
  },
  {
    version: '1.9.1',
    date: '2025-12-27',
    sections: [
      {
        type: 'fixed',
        items: [
          'Fixed blank page when clicking on a user - OffboardWizard was nested incorrectly in Transition.Root',
        ],
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2025-12-27',
    sections: [
      {
        type: 'added',
        items: [
          'User Offboarding Workflow: Multi-step wizard for offboarding users',
          'Block sign-in and revoke active sessions',
          'Reset password to random value',
          'Remove all licenses (frees licenses for reuse)',
          'Share OneDrive with delegate (read/write/owner access levels)',
          'Exchange operations marked for manual completion (convert to shared, grant access, hide from GAL)',
        ],
      },
      {
        type: 'backend',
        items: [
          'New OffboardUser API endpoint',
          'New GetOffboardStatus endpoint',
          'Added OneDrive and license management functions to graphClient.ts',
        ],
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2025-12-27',
    sections: [
      {
        type: 'added',
        items: [
          'Groups Tab: View all tenant groups (Security, M365, Distribution)',
          'Search and filter groups by type',
          'Group properties display (mail-enabled, security-enabled)',
        ],
      },
    ],
  },
  {
    version: '1.7.1',
    date: '2025-12-27',
    sections: [
      {
        type: 'fixed',
        items: [
          'User Detail now works for all tenants, not just those with Azure AD Premium',
          'signInActivity requires Premium - added fallback to v1.0 endpoint',
          'Fixed SWA API routing - removed api_location conflict with linked backend',
        ],
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2025-12-26',
    sections: [
      {
        type: 'added',
        items: [
          'Full User Management: Click any user to view/manage their details',
          'Licenses Tab: View, assign, and remove user licenses',
          'Groups Tab: View group memberships, add/remove from groups',
          'Roles Tab: View Azure AD directory roles (admin roles)',
          'Actions Tab: Reset password, enable/disable account',
          '8 new API endpoints for user management operations',
        ],
      },
    ],
  },
  {
    version: '1.6.1',
    date: '2025-12-27',
    sections: [
      {
        type: 'fixed',
        items: [
          'Users Tab authentication - switched to correct Azure AD app',
          'Fixed GDAP credentials for consolidated cross-tenant app',
          'Resolved Key Vault reference caching issue in SWA',
        ],
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2025-12-26',
    sections: [
      {
        type: 'added',
        items: [
          'Users Tab on Client Detail: View all tenant users with their assigned licenses',
          'New API endpoint GET /clients/{id}/users fetches users via Graph API',
          'UserList component with search and license filter',
          'SKU ID to friendly name mapping using existing subscription data',
        ],
      },
    ],
  },
  {
    version: '1.5.7',
    date: '2025-12-26 20:30 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Self-tenant sync: UME Tech\'s own MS Direct subscriptions now sync automatically',
          'Uses service principal with Organization.Read.All permission for direct Graph API access',
        ],
      },
      {
        type: 'backend',
        items: [
          'New getSelfTenantSubscriptions() function in graphClient.ts',
          'SyncGDAPLicenses now detects and handles self-tenant specially',
          'MS subscription data includes billing status, expiration dates, and trial status',
        ],
      },
    ],
  },
  {
    version: '1.5.6',
    date: '2025-12-26 19:00 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Cancellation Management section now context-aware based on subscription source',
          'Shows "Pax8" instructions for Pax8 subscriptions, "Microsoft Admin Center" for MS Direct',
          'Tooltip and explanation text dynamically update based on where subscription was purchased',
        ],
      },
    ],
  },
  {
    version: '1.5.5',
    date: '2025-12-26 18:45 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Commitment Term no longer incorrectly assumes Monthly billing = Monthly commitment',
          'MS Direct subscriptions with expiration date now correctly show "1-Year (inferred from MS expiration)"',
          'MS Direct without expiration date shows "Unknown - check MS Admin" instead of wrong guess',
        ],
      },
    ],
  },
  {
    version: '1.5.4',
    date: '2025-12-26 18:30 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Renamed "Direct" badge to "MS Direct" for clarity',
          'Pax8 Commitment End now only shows for Pax8-sourced subscriptions (hidden for MS Direct)',
          'MS Direct subscriptions now show "MS Renewal/Expiration" instead of confusing Pax8 dates',
        ],
      },
      {
        type: 'added',
        items: [
          'New "Commitment Term" field showing how long you\'re committed (1-Year vs Monthly)',
          'Explains difference: Billing Term = how often you PAY, Commitment Term = how long you\'re LOCKED IN',
        ],
      },
    ],
  },
  {
    version: '1.5.3',
    date: '2025-12-26 18:15 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Renamed "Commitment End" to "Pax8 Commitment End" with label "(Pax8 pricing term)" to clarify it\'s the Pax8 deal expiration, not Microsoft renewal',
          'Renamed "Expiration Date" to "MS Expiration Date" with "(verified from Microsoft)" indicator',
          'Added "(estimate - verify with MS)" warning when MS date not yet fetched',
          'Improved tooltips to explain the difference between Pax8 commitment and MS renewal dates',
        ],
      },
    ],
  },
  {
    version: '1.5.2',
    date: '2025-12-26 18:00 UTC',
    sections: [
      {
        type: 'fixed',
        items: [
          'Renewal/Expiration date now shows the actual MS expiration date (msNextLifecycleDate) instead of commitment end date',
          'Date field now labeled "Expiration Date" when MS data is available, with "(from MS)" indicator',
        ],
      },
      {
        type: 'added',
        items: [
          'Help tooltips (?) on all subscription detail fields: SKU, Quantity, Billing Term, Commitment End, Expiration Date',
          'Detailed explanation panel in Cancellation Management section explaining what it does',
          'Help tooltips on License Utilization and Quick Info sections',
        ],
      },
    ],
  },
  {
    version: '1.5.1',
    date: '2025-12-26 17:15 UTC',
    sections: [
      {
        type: 'changed',
        items: [
          'Renamed "Renewing/Expiring" to "Recurring: On/Off" to match Microsoft Admin Center terminology',
          'Buttons now say "Set to On" and "Set to Off" for clarity',
        ],
      },
      {
        type: 'added',
        items: [
          'Help tooltip on Recurring Billing Status with step-by-step instructions for checking Microsoft Admin Center',
          'Modification History section on subscription detail page - shows who changed what and when',
          'Audit log integration displaying old → new values for each change',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2025-12-26 16:30 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Customer filter dropdown on Subscriptions page - filter subscriptions by customer/client',
          'Manual auto-renew toggle on subscription detail page - mark subscriptions as "Renewing" or "Expiring"',
          'Three-state auto-renew status: true (Renewing/green), false (Expiring/orange), null (Unknown/gray)',
          'Partner Center API integration (experimental) for autoRenewEnabled field',
        ],
      },
      {
        type: 'changed',
        items: [
          'Auto-renew badges updated: "Renewing" (green), "Expiring" (orange), "Unknown" (gray)',
          'All sync functions now preserve unknown auto-renew state instead of defaulting to true',
          'Subscription detail shows current auto-renew status with manual override buttons',
        ],
      },
      {
        type: 'backend',
        items: [
          'New partnerCenterClient.ts for Partner Center API integration',
          'BillingAuthCallback attempts Partner Center API lookup for auto-renew status',
          'autoRenew type changed from boolean to boolean | null across all sync functions',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2025-12-25 14:00 UTC',
    sections: [
      {
        type: 'added',
        items: [
          'Billing Verification: OAuth flow to sign in as customer Global Admin and fetch MS subscription billing status',
          'New "Verify Billing" button on client detail page',
          'MS billing status fields: msBillingStatus, msNextLifecycleDate, msIsTrial, billingLastChecked',
          'Status "Warning" from MS indicates subscription may not be set to auto-renew',
          'Callback handling for OAuth redirect with success/error notifications',
          'Billing Status section in client sidebar showing verification status and warnings',
          'Changelog page with version history',
        ],
      },
      {
        type: 'backend',
        items: [
          'New GetBillingAuthUrl endpoint to generate OAuth URL for customer tenant',
          'New BillingAuthCallback endpoint to handle OAuth callback and fetch billing data',
          'Added exchangeCodeForToken and getCompanySubscriptionsWithToken functions',
        ],
      },
    ],
  },
  {
    version: '1.3.7',
    date: '2025-12-24',
    sections: [
      {
        type: 'fixed',
        items: ['Exclude suspended subscriptions from license counts (was showing suspended MS records as active)'],
      },
    ],
  },
  {
    version: '1.3.6',
    date: '2025-12-24',
    sections: [
      {
        type: 'added',
        items: [
          'Grouped License Coverage view: Products grouped by normalized name',
          'Shows Pax8 coverage vs unmatched licenses at a glance',
          'Orphaned/GUID records detection with dedicated section',
          'Click to expand and see individual subscriptions per product',
        ],
      },
      {
        type: 'fixed',
        items: ['Removed double-counting of licenses between MS tenant and Pax8 sources'],
      },
    ],
  },
  {
    version: '1.3.5',
    date: '2025-12-23',
    sections: [
      {
        type: 'added',
        items: ['Partner Center API test button for debugging purchase channel info'],
      },
    ],
  },
  {
    version: '1.3.4',
    date: '2025-12-23',
    sections: [
      {
        type: 'changed',
        items: [
          'Client sync buttons now sync only that specific client (not all clients)',
          'Fixed Pax8 product name lookup to use proper API',
        ],
      },
      {
        type: 'added',
        items: ['Power Automate RPA SKU mapping for license display names'],
      },
    ],
  },
  {
    version: '1.3.3',
    date: '2025-12-22',
    sections: [
      {
        type: 'changed',
        items: ['Alerts: suppress renewal alerts for Pax8 subscriptions with auto-renew enabled'],
      },
      {
        type: 'fixed',
        items: ['Backend cleanup of stale/duplicate alerts'],
      },
    ],
  },
  {
    version: '1.3.2',
    date: '2025-12-22',
    sections: [
      {
        type: 'changed',
        items: [
          'Dashboard: exclude free/trial subscriptions from license counts',
          'License gaps: only count Pax8 subscriptions with auto-renew ON as valid coverage',
        ],
      },
      {
        type: 'fixed',
        items: ['Backend alert logic improvements'],
      },
    ],
  },
  {
    version: '1.3.1',
    date: '2025-12-21',
    sections: [
      {
        type: 'fixed',
        items: [
          'Moved at-risk badge to status row for better visibility',
          'Simplified subscription display (removed confusing assigned counts)',
          'Fixed utilization calculation to only use MS Direct data (deduped by SKU)',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2025-12-21',
    sections: [
      {
        type: 'added',
        items: [
          'Client Detail redesign with warning badge for at-risk licenses',
          'Separate Refresh Pax8/MS buttons',
          'Subscription sorting/filtering toolbar',
          'Visual redesign of License Overview section',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2025-12-20',
    sections: [
      {
        type: 'added',
        items: [
          'Enhanced Quick Stats with license breakdown by source (Pax8 vs MS Direct)',
          'Licenses in use visualization',
          'License gap warnings with 90/60/30 day tiered severity alerts',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2025-12-19',
    sections: [
      {
        type: 'added',
        items: ['Sync History tab on Activity Log page', 'Version number display in sidebar'],
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2025-12-18',
    sections: [
      {
        type: 'added',
        items: [
          'Initial release of Pax8 Licensing Portal',
          'Client management with Pax8 company linking',
          'Subscription tracking from Pax8 and Microsoft 365',
          'License utilization monitoring via GDAP',
          'Alert system for renewals and payment issues',
          'Dashboard with key metrics',
          'Audit logging for all changes',
        ],
      },
    ],
  },
];

const sectionLabels = {
  added: { label: 'Added', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  changed: { label: 'Changed', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  fixed: { label: 'Fixed', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  backend: { label: 'Backend', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
};

export default function ChangelogPage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Changelog</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Current version: v{APP_VERSION} (Built: {BUILD_DATE})
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {changelog.map((entry, idx) => (
          <div
            key={entry.version}
            className={`card p-6 ${idx === 0 ? 'border-l-4 border-primary-500' : ''}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                v{entry.version}
                {idx === 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                    Current
                  </span>
                )}
              </h2>
              <span className="text-sm text-muted-foreground">{entry.date}</span>
            </div>

            <div className="space-y-4">
              {entry.sections.map((section, sIdx) => (
                <div key={sIdx}>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      sectionLabels[section.type].color
                    }`}
                  >
                    {sectionLabels[section.type].label}
                  </span>
                  <ul className="mt-2 space-y-1 ml-4">
                    {section.items.map((item, iIdx) => (
                      <li
                        key={iIdx}
                        className="text-sm text-muted-foreground flex items-start"
                      >
                        <span className="text-muted-foreground mr-2">-</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
